import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import { useAuthStore } from '@/features/auth/store';
import { useEditorStore } from '@/features/editor/store';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ProjectCard } from '../components/ProjectCard';
import { TemplateGallery } from '@/features/templates/TemplateGallery';
import * as projectsDB from '@/services/persistence/projects';
import type { Project } from '@/types/diagram';

export function DashboardPage() {
  usePaperTheme();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.id ?? '';

  // "New diagram" / "blank canvas" entries must clear the store before
  // navigating; the editor no longer auto-clears on mount (that used to
  // wipe pre-loaded cloud projects).
  const startBlankDiagram = useCallback(() => {
    useEditorStore.getState().clearCanvas();
    navigate('/editor');
  }, [navigate]);

  const loadProjects = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const list = await projectsDB.listProjects(userId);
    setProjects(list);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleDelete = useCallback(
    async (id: string) => {
      await projectsDB.deleteProject(id, userId);
      setProjects((prev) => prev.filter((p) => p.id !== id));
    },
    [userId],
  );

  return (
    <AppLayout>
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-ink-primary">Projects</h1>
              <p className="mt-1 text-sm text-ink-muted">
                Welcome back, {user?.email}
              </p>
            </div>
            <Button onClick={startBlankDiagram}>New Diagram</Button>
          </div>

          {loading ? (
            <div className="mt-16 flex justify-center">
              <Spinner size="lg" />
            </div>
          ) : projects.length === 0 ? (
            <>
              <div className="mt-10">
                <TemplateGallery title="Start from a template" />
              </div>
              <p className="mt-10 text-center text-petit text-ink-muted [body.paper_&]:text-charcoal-muted">
                Or{' '}
                <button
                  type="button"
                  onClick={startBlankDiagram}
                  className="text-brand-500 hover:text-brand-400 [body.paper_&]:text-teal-600 [body.paper_&]:hover:text-teal-500"
                >
                  start from a blank canvas
                </button>
                .
              </p>
            </>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
              <div className="mt-12">
                <TemplateGallery title="Or start from a template" />
              </div>
            </>
          )}
        </div>
      </main>
    </AppLayout>
  );
}
